import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { HelpCircle, Plus, Send, Clock, CheckCircle, MessageSquare, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const STATUS_MAP = {
  open: { label: 'Open', color: 'bg-blue-500/10 text-blue-500', icon: AlertCircle },
  in_progress: { label: 'In Progress', color: 'bg-orange-500/10 text-orange-500', icon: Clock },
  resolved: { label: 'Resolved', color: 'bg-emerald-500/10 text-emerald-500', icon: CheckCircle },
  closed: { label: 'Closed', color: 'bg-zinc-500/10 text-zinc-500', icon: CheckCircle },
};

const CATEGORIES = [
  { value: 'general', label: 'General' },
  { value: 'billing', label: 'Billing' },
  { value: 'technical', label: 'Technical' },
  { value: 'other', label: 'Other' },
];

export default function Support() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [category, setCategory] = useState('general');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { fetchTickets(); }, []);

  const fetchTickets = async () => {
    try {
      const res = await api.get('/tickets');
      setTickets(res.data.tickets || []);
    } catch { toast.error('Failed to load tickets'); }
    finally { setLoading(false); }
  };

  const handleSubmit = async () => {
    if (!subject.trim() || !message.trim()) { toast.error('Subject and message are required'); return; }
    setSubmitting(true);
    try {
      const res = await api.post('/tickets', { subject, message, category });
      setTickets(prev => [res.data, ...prev]);
      setShowNew(false);
      setSubject('');
      setMessage('');
      setCategory('general');
      toast.success('Ticket submitted! Check your email for confirmation.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create ticket');
    } finally { setSubmitting(false); }
  };

  const statusInfo = (s) => STATUS_MAP[s] || STATUS_MAP.open;

  return (
    <div className="space-y-6" data-testid="support-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Support</h1>
          <p className="text-sm text-muted-foreground">Need help? Submit a ticket and we'll get back to you.</p>
        </div>
        <Button onClick={() => setShowNew(true)} className="bg-orange-500 hover:bg-orange-600" data-testid="new-ticket-btn">
          <Plus className="w-4 h-4 mr-2" /> New Ticket
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground"><HelpCircle className="w-5 h-5 animate-pulse inline mr-2" />Loading...</div>
      ) : tickets.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <MessageSquare className="w-10 h-10 text-muted-foreground/40 mx-auto mb-3" />
            <p className="text-muted-foreground text-sm">No tickets yet</p>
            <p className="text-xs text-muted-foreground/60 mt-1">Submit a ticket to get help from our team</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {tickets.map(t => {
            const si = statusInfo(t.status);
            return (
              <Card key={t.ticket_id} className="cursor-pointer hover:border-primary/30 transition-colors" onClick={() => setSelectedTicket(t)} data-testid={`ticket-${t.ticket_id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-sm truncate">{t.subject}</h3>
                        <Badge className={`text-[10px] ${si.color}`}>{si.label}</Badge>
                        <Badge variant="outline" className="text-[10px]">{t.category}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground line-clamp-1">{t.message}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-[10px] text-muted-foreground">{new Date(t.created_at).toLocaleDateString('fr-FR')}</p>
                      {t.replies?.length > 0 && (
                        <p className="text-[10px] text-orange-500 font-medium mt-0.5">{t.replies.length} reply</p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* New Ticket Dialog */}
      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="sm:max-w-md" data-testid="new-ticket-dialog">
          <DialogHeader>
            <DialogTitle>New Support Ticket</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1.5 block">Category</label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="ticket-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Subject</label>
              <Input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Brief description of your issue" data-testid="ticket-subject-input" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Message</label>
              <textarea
                value={message}
                onChange={e => setMessage(e.target.value)}
                placeholder="Describe your issue in detail..."
                className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none"
                data-testid="ticket-message-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNew(false)}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting} className="bg-orange-500 hover:bg-orange-600" data-testid="submit-ticket-btn">
              <Send className="w-4 h-4 mr-2" />{submitting ? 'Sending...' : 'Submit'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ticket Detail Dialog */}
      <Dialog open={!!selectedTicket} onOpenChange={(o) => { if (!o) setSelectedTicket(null); }}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto" data-testid="ticket-detail-dialog">
          {selectedTicket && (
            <>
              <DialogHeader>
                <DialogTitle className="text-base">{selectedTicket.subject}</DialogTitle>
                <div className="flex items-center gap-2 mt-1">
                  <Badge className={`text-[10px] ${statusInfo(selectedTicket.status).color}`}>{statusInfo(selectedTicket.status).label}</Badge>
                  <Badge variant="outline" className="text-[10px]">{selectedTicket.category}</Badge>
                  <span className="text-[10px] text-muted-foreground">{new Date(selectedTicket.created_at).toLocaleString('fr-FR')}</span>
                </div>
              </DialogHeader>
              <div className="space-y-3 mt-2">
                <div className="bg-muted p-3 rounded-md">
                  <p className="text-xs font-medium text-muted-foreground mb-1">Your message:</p>
                  <p className="text-sm whitespace-pre-wrap">{selectedTicket.message}</p>
                </div>
                {selectedTicket.replies?.map(r => (
                  <div key={r.reply_id} className="border-l-2 border-orange-500 pl-3 py-1">
                    <p className="text-xs font-medium text-orange-500 mb-0.5">{r.from_name || 'Admin'} — {new Date(r.created_at).toLocaleString('fr-FR')}</p>
                    <p className="text-sm whitespace-pre-wrap">{r.message}</p>
                  </div>
                ))}
                {(!selectedTicket.replies || selectedTicket.replies.length === 0) && (
                  <p className="text-xs text-muted-foreground text-center py-4">No replies yet. We'll get back to you soon.</p>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
